import { useMemo } from "react";
import { cn } from "@/lib/utils";

/** FNV-1a: a small, stable 32-bit hash so one org always gets the same texture. */
export function textureSeed(value: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32: a tiny seeded PRNG, so a seed always yields the same map. */
function prng(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const W = 300;
const H = 118;
const CELL = 4;
const LEVELS = 14;

/** Where the texture fades out from, as percentages of the tile, chosen from the seed. */
export function coverTextureFor(seed: string): { x: number; y: number } {
  const h = textureSeed(seed);
  return { x: h % 101, y: (h >>> 8) % 101 };
}

/**
 * A topographic contour map for the seed: two to four hills (the tallest at the
 * fade point) plus a gentle wave, traced into iso-lines with marching squares.
 * Returns one SVG path in a 300 × 118 viewBox.
 */
export function contourPath(seed: string): string {
  const { x, y } = coverTextureFor(seed);
  const rand = prng(textureSeed(seed) ^ 0x9e3779b9);
  const hills = [{ hx: (x / 100) * W, hy: (y / 100) * H, a: 1, sx: 70 + rand() * 50, sy: 40 + rand() * 30 }];
  const extra = 2 + Math.floor(rand() * 3);
  for (let i = 0; i < extra; i++) {
    hills.push({
      hx: rand() * W,
      hy: rand() * H,
      a: (rand() < 0.3 ? -1 : 1) * (0.35 + rand() * 0.45),
      sx: 35 + rand() * 60,
      sy: 25 + rand() * 40,
    });
  }
  const k1 = 0.015 + rand() * 0.03;
  const k2 = 0.02 + rand() * 0.04;
  const p1 = rand() * Math.PI * 2;
  const p2 = rand() * Math.PI * 2;
  const field = (px: number, py: number) => {
    let v = 0.22 * Math.sin(px * k1 + p1) * Math.cos(py * k2 + p2);
    for (const hl of hills) {
      const dx = (px - hl.hx) / hl.sx;
      const dy = (py - hl.hy) / hl.sy;
      v += hl.a * Math.exp(-(dx * dx + dy * dy));
    }
    return v;
  };

  const cols = Math.ceil(W / CELL) + 1;
  const rows = Math.ceil(H / CELL) + 1;
  const grid: number[] = new Array(cols * rows);
  let min = Infinity;
  let max = -Infinity;
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const v = field(i * CELL, j * CELL);
      grid[j * cols + i] = v;
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }

  const r = (n: number) => Math.round(n * 10) / 10;
  const parts: string[] = [];
  for (let l = 1; l <= LEVELS; l++) {
    const t = min + ((max - min) * l) / (LEVELS + 1);
    for (let j = 0; j < rows - 1; j++) {
      for (let i = 0; i < cols - 1; i++) {
        const a = grid[j * cols + i];
        const b = grid[j * cols + i + 1];
        const c = grid[(j + 1) * cols + i + 1];
        const d = grid[(j + 1) * cols + i];
        const x0 = i * CELL;
        const y0 = j * CELL;
        // Edge crossings, linearly interpolated: top, right, bottom, left.
        const pts: [number, number][] = [];
        if (a < t !== b < t) pts.push([x0 + ((t - a) / (b - a)) * CELL, y0]);
        if (b < t !== c < t) pts.push([x0 + CELL, y0 + ((t - b) / (c - b)) * CELL]);
        if (d < t !== c < t) pts.push([x0 + ((t - d) / (c - d)) * CELL, y0 + CELL]);
        if (a < t !== d < t) pts.push([x0, y0 + ((t - a) / (d - a)) * CELL]);
        for (let k = 0; k + 1 < pts.length; k += 2) {
          parts.push(`M${r(pts[k][0])} ${r(pts[k][1])}L${r(pts[k + 1][0])} ${r(pts[k + 1][1])}`);
        }
      }
    }
  }
  return parts.join("");
}

/**
 * Bright UI: a quiet per-org texture for covers with no photo — a fine teal dot
 * grid under thin topographic contour lines, both fading out from a spot chosen
 * from the seed, so neighbouring cards never match. Absolutely positioned;
 * place it inside a `relative` tile, under the monogram or logo.
 */
export default function CoverTexture({ seed, className }: { seed: string; className?: string }) {
  const { x, y } = coverTextureFor(seed);
  const d = useMemo(() => contourPath(seed), [seed]);
  const fade = (reach: number) => `radial-gradient(ellipse at ${x}% ${y}%, #000 0, transparent ${reach}%)`;
  return (
    <div aria-hidden data-cover-texture className={cn("pointer-events-none absolute inset-0", className)}>
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(hsl(var(--teal-primary)) 1px, transparent 1.2px)",
          backgroundSize: "12px 12px",
          opacity: 0.16,
          WebkitMaskImage: fade(75),
          maskImage: fade(75),
        }}
      />
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 h-full w-full text-teal-primary"
        style={{ WebkitMaskImage: fade(85), maskImage: fade(85) }}
      >
        <path d={d} fill="none" stroke="currentColor" strokeOpacity={0.09} strokeWidth={0.7} strokeLinecap="round" />
      </svg>
    </div>
  );
}
